import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import type { AuthResponse } from '../../../../front/src/types/auth';

const STORAGE_KEY_PREFIX = 'graphdebate:native-auth';

// Build a deterministic Preferences key so every website gets its own slot.
const makeStorageKey = (websiteKey: string) => `${STORAGE_KEY_PREFIX}:${websiteKey}`;

const isCapacitorNative = () => Capacitor.isNativePlatform();

export type NativeStoredTokens = Pick<AuthResponse, 'token' | 'refreshToken'> & {
  updatedAt: number;
};

/**
 * Persist the latest GraphLogin tokens inside Capacitor Preferences so the native shell
 * can revive the session even if the WebView was killed or the cookies expired.
 */
export async function saveNativeTokens(
  websiteKey: string,
  { token, refreshToken }: Pick<AuthResponse, 'token' | 'refreshToken'>,
) {
  if (!isCapacitorNative()) return;
  if (!websiteKey) return;

  try {
    const payload: NativeStoredTokens = {
      token,
      refreshToken,
      updatedAt: Date.now(),
    };

    await Preferences.set({
      key: makeStorageKey(websiteKey),
      value: JSON.stringify(payload),
    });
    console.log('🔐 Native tokens saved in Preferences');
  } catch (error) {
    console.error('Failed to persist native tokens', error);
  }
}

/**
 * Read the serialized tokens from Preferences. Returns undefined if nothing was stored yet.
 */
export async function loadNativeTokens(websiteKey: string) {
  if (!isCapacitorNative()) return undefined;
  if (!websiteKey) return undefined;

  try {
    const { value } = await Preferences.get({ key: makeStorageKey(websiteKey) });
    if (!value) return undefined;
    return JSON.parse(value) as NativeStoredTokens;
  } catch (error) {
    console.error('Failed to read native tokens', error);
    return undefined;
  }
}

/**
 * Utility to erase the saved tokens (e.g. on logout) so the native shell does not rehydrate
 * an obsolete session.
 */
export async function clearNativeTokens(websiteKey: string) {
  if (!isCapacitorNative()) return;
  if (!websiteKey) return;

  try {
    await Preferences.remove({ key: makeStorageKey(websiteKey) });
    console.log('🔐 Native tokens cleared from Preferences');
  } catch (error) {
    console.error('Failed to clear native tokens', error);
  }
}
